import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BosDurum, Buton, HazirAvatar, Iskelet, cn, hazirAvatarAdi } from "@koclukpanel/ui";
import {
  ApiHatasi,
  avatarFotoYukle,
  avatarim,
  avatarimFotoBaglantisi,
  hazirAvatarSayisi,
  hazirAvatarSec,
  type AvatarGorunumu,
} from "./api";

/**
 * PROFİL GÖRSELİM — F-PPL-13, öğrenci ve veli tarafı
 *
 * ================================================================
 * UÇLAR AÇIKTI, EKRAN YOKTU
 * ================================================================
 * `avatar.controller.ts` hiçbir uçta izin sormuyor: kişi yalnızca
 * KENDİ görselini değiştiriyor ve kimlik gövdeden değil bağlamdan
 * alınıyor. Yani öğrenci ve veli bunları 04.09.2026'dan beri
 * çağırabiliyordu; eksik olan tek şey onları çağıran bir ekrandı.
 *
 * Ölçüldü (07.09.2026): `apps/ogrenci` içinde "avatar" kelimesi HİÇ
 * geçmiyordu. Şartname *"her kullanıcı kendi profil görselini
 * belirleyebilir"* diyor ve bu sistemin kullanıcılarının çoğunluğu
 * öğrenci.
 *
 * F-NTF-09'un (bildirim gelen kutusu) birebir aynı sınıfı: mekanizma
 * var, ona giden yol yok.
 *
 * ================================================================
 * DİL KİPİ — ÖĞRENCİYE "SEN", VELİYE "SİZ"
 * ================================================================
 * `Mesajlar.tsx` ve `Bildirimlerim.tsx` ile aynı desen ve aynı
 * gerekçe (`VeliKabugu.tsx`: *"Veli öğrenci değil."*). Sözlük olarak
 * duruyor ki bir metin eklerken ötekini yazmayı unutmak DERLEME
 * hatası versin.
 */
type Kip = "sen" | "siz";

interface KipMetni {
  aciklama: string;
  gorselYok: string;
  fotoEtiketi: string;
  kaldirUyarisi: string;
}

const METIN: Record<Kip, KipMetni> = {
  sen: {
    aciklama: "Görselini yalnızca sen değiştirebilirsin.",
    gorselYok: "Görsel seçmedin; baş harflerin gösteriliyor.",
    fotoEtiketi: "Kendi fotoğrafın",
    kaldirUyarisi:
      "Uygunsuz bir görsel yüklersen kurum onu kaldırabilir; yerine yenisini seçebilirsin.",
  },
  siz: {
    aciklama: "Görselinizi yalnızca siz değiştirebilirsiniz.",
    gorselYok: "Görsel seçmediniz; baş harfleriniz gösteriliyor.",
    fotoEtiketi: "Kendi fotoğrafınız",
    kaldirUyarisi:
      "Uygunsuz bir görsel yüklerseniz kurum onu kaldırabilir; yerine yenisini seçebilirsiniz.",
  },
};

export function ProfilGorselim({ kip = "sen", onGeri }: { kip?: Kip; onGeri?: () => void } = {}) {
  const qc = useQueryClient();

  const gorsel = useQuery({ queryKey: ["avatarim"], queryFn: avatarim, retry: false });
  const hazirlar = useQuery({
    queryKey: ["hazir-avatar-sayisi"],
    queryFn: hazirAvatarSayisi,
    retry: false,
  });

  const tazele = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["avatarim"] });
    await qc.invalidateQueries({ queryKey: ["avatar-foto"] });
  };

  const sec = useMutation({
    mutationFn: (secim: number | null) => hazirAvatarSec(secim),
    onSuccess: tazele,
  });

  const yukle = useMutation({
    mutationFn: (dosya: File) => avatarFotoYukle(dosya),
    onSuccess: tazele,
  });

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        {/*
          Geri düğmesi YALNIZCA çağıran isterse — öğrencide bu ekran
          `Hesabım`ın alt ekranı ve geri düğmesi ORADA duruyor.
          `Mesajlar.tsx` ile aynı kural.
        */}
        {onGeri && (
          <Buton gorunum="sessiz" onClick={onGeri}>
            ← Geri
          </Buton>
        )}
        <h1 className="text-notr-900 text-lg font-semibold">Profil görselim</h1>
      </div>

      {gorsel.isPending ? (
        <Iskelet satir={3} />
      ) : gorsel.isError ? (
        <BosDurum
          ton="hata"
          baslik="Görsel bilgisi alınamadı"
          aciklama={gorsel.error instanceof ApiHatasi ? gorsel.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void gorsel.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      ) : (
        <>
          <div className="flex items-center gap-4">
            <Onizleme gorsel={gorsel.data} />
            <p className="text-notr-700 text-sm">
              {gorsel.data.fotoVarMi
                ? "Yüklenmiş bir fotoğraf var."
                : gorsel.data.avatarPreset !== null
                  ? `${hazirAvatarAdi(gorsel.data.avatarPreset)} avatarı seçili.`
                  : METIN[kip].gorselYok}
            </p>
          </div>

          {(hazirlar.data?.adet ?? 0) > 0 && (
            <fieldset className="flex flex-col gap-2">
              <legend className="text-notr-800 mb-1 text-sm font-medium">Hazır avatarlar</legend>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: hazirlar.data?.adet ?? 0 }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`${hazirAvatarAdi(i)} avatarını seç`}
                    aria-pressed={gorsel.data.avatarPreset === i}
                    disabled={sec.isPending}
                    onClick={() => sec.mutate(i)}
                    className={cn(
                      "min-h-dokunma min-w-dokunma flex items-center justify-center rounded-full p-1",
                      gorsel.data.avatarPreset === i
                        ? "bg-marka-600 text-notr-0 ring-marka-600 ring-2 ring-offset-2"
                        : "bg-notr-100 text-notr-700",
                    )}
                  >
                    <HazirAvatar secim={i} boyut={40} aria-hidden />
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {/*
            Ham `<input type="file">`: `Alan` DENETİMLİ bir alan ve
            dosya alanının `value`si GÜVENLİK GEREĞİ koddan atanamıyor
            — tarayıcı izin vermiyor. Muafiyet `form-bileseni.test.ts`
            içinde yazılı ve biçim kurallarını KALDIRMIYOR: 44px
            dokunma hedefi ve etiket bağı burada elle sağlanıyor.
          */}
          <div className="flex flex-col gap-2">
            <label htmlFor="ogrenci-avatar-foto" className="text-notr-800 text-sm font-medium">
              {METIN[kip].fotoEtiketi}
            </label>
            <input
              id="ogrenci-avatar-foto"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={yukle.isPending}
              onChange={(e) => {
                const d = e.target.files?.[0];
                /*
                  Alan SEÇİMDEN SONRA temizleniyor: aynı dosya ikinci
                  kez seçildiğinde `change` olayı hiç tetiklenmez ve
                  kullanıcı "bir şey olmadı" görürdü.
                */
                e.target.value = "";
                if (d) yukle.mutate(d);
              }}
              className="min-h-dokunma text-notr-700 file:rounded-alan file:border-notr-200 file:bg-notr-50 file:text-notr-800 text-sm file:mr-3 file:border file:px-3 file:py-2 file:text-sm"
            />
            <p className="text-notr-600 text-xs">
              JPEG, PNG ya da WebP · en fazla 2 MB. Yeni fotoğraf eskisinin yerine geçer.
            </p>
          </div>

          {(sec.isError || yukle.isError) && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {(sec.error ?? yukle.error) instanceof ApiHatasi
                ? ((sec.error ?? yukle.error) as ApiHatasi).message
                : "İşlem tamamlanamadı."}
            </p>
          )}

          {(gorsel.data.fotoVarMi || gorsel.data.avatarPreset !== null) && (
            <div>
              <Buton gorunum="ikincil" yukleniyor={sec.isPending} onClick={() => sec.mutate(null)}>
                Görseli kaldır
              </Buton>
            </div>
          )}

          <p className="text-notr-600 text-xs">
            {METIN[kip].aciklama} {METIN[kip].kaldirUyarisi}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Görselin önizlemesi — üç hâl, ve hiçbirinde BOŞ ÇERÇEVE yok.
 *
 * Şartnamenin kendi cümlesi: *"Görsel yoksa ad-soyad baş harfleri
 * gösterilir; boş çerçeve çizilmez."* Boş çerçeve, yüklenmemiş bir
 * görsel ile yüklenememiş bir görseli aynı gösterirdi.
 *
 * Bağlantı ALINAMAZSA da baş harflere düşülüyor: kırık bir resim
 * simgesi, görselin olmadığını değil sistemin bozuk olduğunu söylerdi.
 */
function Onizleme({ gorsel }: { gorsel: AvatarGorunumu }) {
  const foto = useQuery({
    queryKey: ["avatar-foto"],
    queryFn: avatarimFotoBaglantisi,
    enabled: gorsel.fotoVarMi,
    retry: false,
    // Bağlantı 15 dakika yaşıyor; 10 dakikada bayatlıyor ki ekranda
    // süresi dolmuş bir bağlantı kalmasın.
    staleTime: 10 * 60 * 1000,
  });

  if (gorsel.fotoVarMi && foto.data) {
    return <img src={foto.data.url} alt="" className="size-16 rounded-full object-cover" />;
  }

  if (gorsel.avatarPreset !== null) {
    return <HazirAvatar secim={gorsel.avatarPreset} boyut={64} />;
  }

  return (
    <div className="bg-notr-200 text-notr-700 flex size-16 items-center justify-center rounded-full text-xl font-semibold">
      {/* Baş harf de yoksa tire: boş bir daire "yükleniyor" gibi okunurdu. */}
      {gorsel.basHarfler || "—"}
    </div>
  );
}
